/**
 * Enrollment Wizard
 *
 * A guided, step-by-step enrollment experience for parents.
 * Steps:
 * 1. Select Swimmer - Choose which child to enroll
 * 2. Select Class - Browse and select an available class
 * 3. Review & Confirm - Review details and confirm enrollment
 */

import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useState } from "react"
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiMapPin,
  FiUser,
  FiUsers,
} from "react-icons/fi"

import { useClassTemplates } from "@/hooks/api/useClassTemplates"
import { useCreateEnrollment } from "@/hooks/api/useEnrollments"
import { useSessions } from "@/hooks/api/useSessions"
import { useStudents } from "@/hooks/api/useStudents"
import type { ClassTemplateResponse } from "@/types/class_template"
import type { SessionResponse } from "@/types/session"
import type { StudentResponse } from "@/types/student"

// Step indicator component
function StepIndicator({
  step,
  currentStep,
  label,
}: {
  step: number
  currentStep: number
  label: string
}) {
  const isCompleted = currentStep > step
  const isCurrent = currentStep === step

  return (
    <VStack gap={1}>
      <Box
        w="10"
        h="10"
        borderRadius="full"
        bg={isCompleted ? "green.500" : isCurrent ? "ui.main" : "gray.200"}
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontWeight="bold"
        fontSize="sm"
      >
        {isCompleted ? <FiCheck /> : step}
      </Box>
      <Text
        fontSize="xs"
        fontWeight={isCurrent ? "semibold" : "normal"}
        color={isCurrent ? "ui.main" : "gray.500"}
      >
        {label}
      </Text>
    </VStack>
  )
}

// Calculate age from date of birth
function calculateAge(dateOfBirth: string): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age
}

// Format time from 24h to 12h
function formatTime(time: string | null): string {
  if (!time) return ""
  const [hours, minutes] = time.split(":")
  const hour = Number.parseInt(hours, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

// Format currency
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

// Student selection card
function StudentSelectionCard({
  student,
  isSelected,
  onSelect,
}: {
  student: StudentResponse
  isSelected: boolean
  onSelect: () => void
}) {
  const age = calculateAge(student.date_of_birth)

  return (
    <Card.Root
      bg={isSelected ? "ui.backgroundAlt" : "white"}
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="2px"
      borderColor={isSelected ? "ui.main" : "gray.100"}
      _hover={{ borderColor: "ui.main", boxShadow: "md" }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={onSelect}
    >
      <Card.Body p={4}>
        <HStack gap={4}>
          <Box
            w="48px"
            h="48px"
            borderRadius="full"
            bg={isSelected ? "ui.main" : "gray.100"}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {isSelected ? (
              <Icon color="white" fontSize="xl">
                <FiCheck />
              </Icon>
            ) : (
              <Icon color="gray.400" fontSize="xl">
                <FiUser />
              </Icon>
            )}
          </Box>
          <Box flex={1}>
            <Text fontWeight="semibold" color="gray.800">
              {student.first_name} {student.last_name}
            </Text>
            <HStack fontSize="sm" color="gray.500" gap={3}>
              <Text>{age} years old</Text>
              {student.current_swim_level && (
                <Badge colorPalette="cyan" variant="subtle" size="sm">
                  {student.current_swim_level}
                </Badge>
              )}
            </HStack>
          </Box>
          {student.has_asthma && (
            <Icon color="orange.500">
              <FiAlertCircle />
            </Icon>
          )}
        </HStack>
      </Card.Body>
    </Card.Root>
  )
}

// Class selection card
function ClassSelectionCard({
  classTemplate,
  session,
  isSelected,
  onSelect,
  isMidSession,
}: {
  classTemplate: ClassTemplateResponse
  session: SessionResponse | null
  isSelected: boolean
  onSelect: () => void
  isMidSession?: boolean
}) {
  const current = classTemplate.current_enrollment ?? 0
  const max = classTemplate.max_students ?? 0
  const spotsAvailable = max - current
  const isFull = spotsAvailable <= 0

  const totalPrice = session
    ? (classTemplate.price || 0) * (session.total_classes || 1)
    : classTemplate.price || 0

  return (
    <Card.Root
      bg={isSelected ? "ui.backgroundAlt" : "white"}
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="2px"
      borderColor={isSelected ? "ui.main" : "gray.100"}
      _hover={{ borderColor: "ui.main", boxShadow: "md" }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={onSelect}
    >
      <Card.Body p={4}>
        <VStack align="stretch" gap={3}>
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                {classTemplate.name}
              </Text>
              {classTemplate.swim_level && (
                <Badge colorPalette="blue" variant="subtle" mt={1}>
                  {classTemplate.swim_level}
                </Badge>
              )}
            </Box>
            {isSelected && (
              <Box bg="ui.main" color="white" p={1} borderRadius="full">
                <FiCheck />
              </Box>
            )}
          </Flex>

          <VStack align="stretch" gap={2}>
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiCalendar />
              </Icon>
              <Text>
                {classTemplate.day_of_week}{" "}
                {formatTime(classTemplate.start_time)} -{" "}
                {formatTime(classTemplate.end_time)}
              </Text>
            </HStack>

            {classTemplate.location && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiMapPin />
                </Icon>
                <Text>{classTemplate.location}</Text>
              </HStack>
            )}

            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiUsers />
              </Icon>
              <Text>
                {isFull
                  ? classTemplate.waitlist_enabled
                    ? "Full - Waitlist available"
                    : "Full"
                  : `${spotsAvailable} spots available`}
              </Text>
            </HStack>
          </VStack>

          <Flex
            justify="space-between"
            align="center"
            pt={2}
            borderTopWidth="1px"
            borderColor="gray.100"
          >
            <VStack align="start" gap={0}>
              <Text fontWeight="bold" fontSize="lg" color="ui.main">
                {isMidSession
                  ? `Up to ${formatCurrency(totalPrice)}`
                  : formatCurrency(totalPrice)}
              </Text>
              {session && (
                <Text fontSize="xs" color="gray.500">
                  {formatCurrency(classTemplate.price || 0)}/class ×{" "}
                  {session.total_classes} classes
                  {isMidSession && " (pro-rated)"}
                </Text>
              )}
            </VStack>
            <Badge colorPalette={isFull ? "orange" : "green"} variant="subtle">
              {isFull ? "Waitlist" : "Available"}
            </Badge>
          </Flex>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export interface EnrollmentWizardProps {
  onComplete?: () => void
  onCancel?: () => void
}

export function EnrollmentWizard({
  onComplete,
  onCancel,
}: EnrollmentWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  )
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  )
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null)

  // Fetch data
  const { data: studentsData, isLoading: studentsLoading } = useStudents()
  const { data: sessionsData, isLoading: sessionsLoading } = useSessions()
  const { data: classTemplatesData, isLoading: classTemplatesLoading } =
    useClassTemplates()

  const createEnrollment = useCreateEnrollment()

  const students = studentsData?.items || []
  const sessions = sessionsData?.items || []
  const classTemplates = classTemplatesData?.items || []

  // Filter out ended sessions - only show sessions where end_week >= today
  const today = new Date().toISOString().split("T")[0]
  const availableSessions = sessions.filter((s) => s.end_week >= today)

  // Filter to active/published classes
  const availableClasses = classTemplates.filter(
    (c) => c.is_active !== false && c.is_published !== false,
  )

  // Get selected items
  const selectedStudent = students.find((s) => s.id === selectedStudentId)
  const selectedSession = sessions.find((s) => s.id === selectedSessionId)
  const selectedClass = classTemplates.find((c) => c.id === selectedClassId)

  // Calculate total price (estimate - backend will pro-rate for mid-session)
  const isMidSession = selectedSession && selectedSession.start_week < today
  const totalPrice =
    selectedSession && selectedClass
      ? (selectedClass.price || 0) * (selectedSession.total_classes || 1)
      : 0

  // Check if enrollment will be waitlisted
  const current = selectedClass?.current_enrollment ?? 0
  const max = selectedClass?.max_students ?? 0
  const willBeWaitlisted = current >= max

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    if (!selectedStudentId || !selectedSessionId || !selectedClassId) return

    try {
      await createEnrollment.mutateAsync({
        student_id: selectedStudentId,
        session_id: selectedSessionId,
        class_template_id: selectedClassId,
        notes: null,
        invoice_data: {
          amount_due: totalPrice,
          amount_paid: 0,
          payment_status: "pending",
          payment_method: null,
          payment_date: null,
          invoice_number: null,
          notes: null,
        },
      })
      onComplete?.()
    } catch (error) {
      // Error handling is done by the mutation hook
      console.error("Enrollment failed:", error)
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!selectedStudentId
      case 2:
        return !!selectedSessionId && !!selectedClassId
      case 3:
        return true
      default:
        return false
    }
  }

  const isLoading = studentsLoading || sessionsLoading || classTemplatesLoading

  if (isLoading) {
    return (
      <Container maxW="4xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <VStack gap={4} py={16}>
          <Spinner size="xl" color="ui.main" />
          <Text color="gray.500">Loading enrollment options...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="4xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
      <VStack gap={{ base: 4, md: 8 }} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading size={{ base: "lg", md: "xl" }} color="gray.800" mb={2}>
            Enroll in a Class
          </Heading>
          <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
            Follow the steps below to enroll your swimmer
          </Text>
        </Box>

        {/* Step Indicator */}
        <Flex justify="center" gap={{ base: 3, md: 8 }}>
          <StepIndicator step={1} currentStep={currentStep} label="Swimmer" />
          <Box
            flex={1}
            maxW={{ base: "40px", md: "100px" }}
            h="1px"
            bg="gray.200"
            alignSelf="center"
            mt={-4}
          />
          <StepIndicator step={2} currentStep={currentStep} label="Class" />
          <Box
            flex={1}
            maxW={{ base: "40px", md: "100px" }}
            h="1px"
            bg="gray.200"
            alignSelf="center"
            mt={-4}
          />
          <StepIndicator step={3} currentStep={currentStep} label="Confirm" />
        </Flex>

        {/* Step Content */}
        <Card.Root bg="white" borderRadius="xl" boxShadow="md">
          <Card.Body p={{ base: 3, md: 6 }}>
            {/* Step 1: Select Swimmer */}
            {currentStep === 1 && (
              <VStack align="stretch" gap={6}>
                <Box>
                  <Heading size="md" color="gray.800" mb={2}>
                    Select a Swimmer
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    Choose which child you'd like to enroll in a class
                  </Text>
                </Box>

                {students.length === 0 ? (
                  <Box
                    bg="orange.50"
                    borderRadius="lg"
                    p={6}
                    textAlign="center"
                  >
                    <Icon color="orange.500" fontSize="3xl" mb={3}>
                      <FiUsers />
                    </Icon>
                    <Text fontWeight="medium" color="gray.800" mb={2}>
                      No swimmers found
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      Please add a swimmer to your family profile first
                    </Text>
                  </Box>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    {students.map((student) => (
                      <StudentSelectionCard
                        key={student.id}
                        student={student}
                        isSelected={selectedStudentId === student.id}
                        onSelect={() => setSelectedStudentId(student.id)}
                      />
                    ))}
                  </SimpleGrid>
                )}
              </VStack>
            )}

            {/* Step 2: Select Class */}
            {currentStep === 2 && (
              <VStack align="stretch" gap={6}>
                <Box>
                  <Heading size="md" color="gray.800" mb={2}>
                    Choose a Class
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    Select a session and class for {selectedStudent?.first_name}
                  </Text>
                </Box>

                {/* Session Selection */}
                <Box>
                  <Text fontWeight="medium" color="gray.700" mb={3}>
                    Session / Term
                  </Text>
                  {availableSessions.length === 0 ? (
                    <Box
                      bg="orange.50"
                      borderRadius="lg"
                      p={6}
                      textAlign="center"
                    >
                      <Text fontWeight="medium" color="gray.800" mb={2}>
                        No active sessions
                      </Text>
                      <Text color="gray.600" fontSize="sm">
                        There are no current or upcoming sessions available for
                        enrollment. Please check back later.
                      </Text>
                    </Box>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
                      {availableSessions.map((session) => {
                        const isActive =
                          session.start_week <= today &&
                          session.end_week >= today

                        return (
                          <Box
                            key={session.id}
                            p={4}
                            borderRadius="lg"
                            borderWidth="2px"
                            borderColor={
                              selectedSessionId === session.id
                                ? "ui.main"
                                : "gray.200"
                            }
                            bg={
                              selectedSessionId === session.id
                                ? "ui.backgroundAlt"
                                : "white"
                            }
                            cursor="pointer"
                            onClick={() => setSelectedSessionId(session.id)}
                            _hover={{ borderColor: "ui.main" }}
                            transition="all 0.2s"
                          >
                            <Flex justify="space-between" align="start">
                              <Text fontWeight="semibold" color="gray.800">
                                {session.name}
                              </Text>
                              <Badge
                                colorPalette={isActive ? "green" : "blue"}
                                variant="subtle"
                                size="sm"
                              >
                                {isActive ? "Active" : "Upcoming"}
                              </Badge>
                            </Flex>
                            <HStack fontSize="sm" color="gray.500" mt={1}>
                              <FiClock />
                              <Text>{session.total_classes} classes</Text>
                            </HStack>
                            {isActive && (
                              <Text fontSize="xs" color="gray.400" mt={1}>
                                Mid-session pricing may apply
                              </Text>
                            )}
                          </Box>
                        )
                      })}
                    </SimpleGrid>
                  )}
                </Box>

                {/* Class Selection */}
                {selectedSessionId && (
                  <Box>
                    <Text fontWeight="medium" color="gray.700" mb={3}>
                      Available Classes
                    </Text>
                    {availableClasses.length === 0 ? (
                      <Box
                        bg="gray.50"
                        borderRadius="lg"
                        p={6}
                        textAlign="center"
                      >
                        <Text color="gray.600">
                          No classes available for this session
                        </Text>
                      </Box>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        {availableClasses.map((classTemplate) => (
                          <ClassSelectionCard
                            key={classTemplate.id}
                            classTemplate={classTemplate}
                            session={selectedSession ?? null}
                            isSelected={selectedClassId === classTemplate.id}
                            onSelect={() =>
                              setSelectedClassId(classTemplate.id)
                            }
                            isMidSession={!!isMidSession}
                          />
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                )}
              </VStack>
            )}

            {/* Step 3: Review & Confirm */}
            {currentStep === 3 && (
              <VStack align="stretch" gap={6}>
                <Box>
                  <Heading size="md" color="gray.800" mb={2}>
                    Review & Confirm
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    Please review the enrollment details before confirming
                  </Text>
                </Box>

                {/* Enrollment Summary */}
                <VStack align="stretch" gap={4}>
                  {/* Swimmer */}
                  <Box bg="gray.50" borderRadius="lg" p={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.500"
                      mb={2}
                    >
                      SWIMMER
                    </Text>
                    <HStack>
                      <Icon color="ui.main">
                        <FiUser />
                      </Icon>
                      <Text fontWeight="semibold" color="gray.800">
                        {selectedStudent?.first_name}{" "}
                        {selectedStudent?.last_name}
                      </Text>
                      {selectedStudent?.current_swim_level && (
                        <Badge colorPalette="cyan" variant="subtle">
                          {selectedStudent.current_swim_level}
                        </Badge>
                      )}
                    </HStack>
                  </Box>

                  {/* Class */}
                  <Box bg="gray.50" borderRadius="lg" p={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.500"
                      mb={2}
                    >
                      CLASS
                    </Text>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      {selectedClass?.name}
                    </Text>
                    <VStack align="stretch" gap={1}>
                      <HStack fontSize="sm" color="gray.600">
                        <FiCalendar />
                        <Text>
                          {selectedClass?.day_of_week}{" "}
                          {formatTime(selectedClass?.start_time || null)} -{" "}
                          {formatTime(selectedClass?.end_time || null)}
                        </Text>
                      </HStack>
                      {selectedClass?.location && (
                        <HStack fontSize="sm" color="gray.600">
                          <FiMapPin />
                          <Text>{selectedClass.location}</Text>
                        </HStack>
                      )}
                      <HStack fontSize="sm" color="gray.600">
                        <FiClock />
                        <Text>
                          {selectedSession?.name} (
                          {selectedSession?.total_classes} classes)
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Pricing */}
                  <Box
                    bg={willBeWaitlisted ? "orange.50" : "green.50"}
                    borderRadius="lg"
                    p={4}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.500"
                      mb={2}
                    >
                      {willBeWaitlisted ? "WAITLIST ENROLLMENT" : "TOTAL COST"}
                    </Text>
                    <Flex
                      justify="space-between"
                      align={{ base: "start", sm: "center" }}
                      direction={{ base: "column", sm: "row" }}
                      gap={{ base: 2, sm: 0 }}
                    >
                      <VStack align="start" gap={0}>
                        <HStack>
                          <Icon
                            color={
                              willBeWaitlisted ? "orange.500" : "green.500"
                            }
                          >
                            <FiCreditCard />
                          </Icon>
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontWeight="bold"
                            color={
                              willBeWaitlisted ? "orange.600" : "green.600"
                            }
                          >
                            {isMidSession
                              ? `Up to ${formatCurrency(totalPrice)}`
                              : formatCurrency(totalPrice)}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {formatCurrency(selectedClass?.price || 0)}/class ×{" "}
                          {selectedSession?.total_classes} classes
                          {isMidSession && " (pro-rated at checkout)"}
                        </Text>
                      </VStack>
                      <Badge
                        colorPalette={willBeWaitlisted ? "orange" : "green"}
                        variant="subtle"
                        px={3}
                        py={1}
                      >
                        {willBeWaitlisted ? "Waitlist" : "Confirmed"}
                      </Badge>
                    </Flex>
                    {willBeWaitlisted && (
                      <Text fontSize="sm" color="orange.600" mt={3}>
                        This class is currently full. You'll be added to the
                        waitlist and notified when a spot opens up.
                      </Text>
                    )}
                    {!willBeWaitlisted && (
                      <Text fontSize="sm" color="green.600" mt={3}>
                        Payment can be made via Zelle, cash, or check.
                        Instructions will be provided after enrollment.
                      </Text>
                    )}
                  </Box>
                </VStack>
              </VStack>
            )}
          </Card.Body>
        </Card.Root>

        {/* Navigation */}
        <Flex
          justify="space-between"
          direction={{ base: "column-reverse", sm: "row" }}
          gap={{ base: 2, sm: 0 }}
        >
          <Button
            variant="ghost"
            onClick={currentStep === 1 ? onCancel : handleBack}
            disabled={createEnrollment.isPending}
            minH="44px"
            w={{ base: "100%", sm: "auto" }}
          >
            <FiChevronLeft />
            {currentStep === 1 ? "Cancel" : "Back"}
          </Button>

          {currentStep < 3 ? (
            <Button
              colorPalette="blue"
              onClick={handleNext}
              disabled={!canProceed()}
              minH="44px"
              w={{ base: "100%", sm: "auto" }}
            >
              Continue
              <FiChevronRight />
            </Button>
          ) : (
            <Button
              colorPalette="green"
              onClick={handleSubmit}
              loading={createEnrollment.isPending}
              loadingText="Enrolling..."
              disabled={!canProceed()}
              minH="44px"
              w={{ base: "100%", sm: "auto" }}
            >
              <FiCheckCircle />
              Confirm Enrollment
            </Button>
          )}
        </Flex>
      </VStack>
    </Container>
  )
}

export default EnrollmentWizard
